/**
 * Рынки для сита: не одно семейство на вилку, а набор меток по трём осям —
 * что за ставка (исход, фора, тотал, инд. тотал…), на каком отрезке (матч,
 * карта N, сегмент карты) и редкие исходы (гонка до N, N-й объект, пропсы).
 *
 * Состав снят с эпизодов зеркала и движка за 24–27.09: форы и тоталы по
 * раундам на картах, индивидуальные тоталы (36 тыс. эпизодов за 3 дня),
 * «победа в сегменте карты» `WIN::PS:k:n` (6.5 тыс.), гонки до N и N-й объект.
 */
import type { Fork } from './protocol';
import { isMap3, isMap4, isSetSport, marketPeriod } from './sports';

export type MarketMode = 'only' | 'except';

export type MarketGroup = { id: string; name: string; tags: readonly { code: string; name: string }[] };

/** Оси «что за ставка» и «отрезок»: в режиме «только эти» оси складываются через И. */
export const MARKET_GROUPS: readonly MarketGroup[] = [
  {
    id: 'kind',
    name: 'ставка',
    tags: [
      { code: 'win', name: 'победа' },
      { code: 'hcp_maps', name: 'фора по картам' },
      { code: 'hcp_pts', name: 'фора раундов / очков' },
      { code: 'ttl_maps', name: 'тотал карт' },
      { code: 'ttl_pts', name: 'тотал раундов / очков' },
      { code: 'ittl', name: 'инд. тотал' },
      { code: 'double', name: 'двойной шанс' },
    ],
  },
  {
    id: 'span',
    name: 'отрезок',
    tags: [
      { code: 'whole', name: 'весь матч' },
      { code: 'map1', name: 'карта 1' },
      { code: 'map2', name: 'карта 2' },
      { code: 'map3', name: 'исход 3 карты' },
      { code: 'map4', name: 'исход 4 карты' },
      { code: 'map5', name: 'карта 5' },
      { code: 'part', name: 'тайм / четверть / сет' },
    ],
  },
  {
    id: 'odd',
    name: 'редкие',
    tags: [
      { code: 'segment', name: 'сегмент карты' },
      { code: 'race', name: 'гонка до N' },
      { code: 'nth', name: 'N-й объект' },
      { code: 'prop', name: 'пропсы' },
      { code: 'corners', name: 'угловые' },
      { code: 'cards', name: 'карточки' },
      { code: 'btts', name: 'обе забьют' },
      { code: 'other', name: 'прочее' },
    ],
  },
];

export const MARKET_NAMES: Record<string, string> = Object.fromEntries(
  MARKET_GROUPS.flatMap((group) => group.tags.map((tag) => [tag.code, tag.name])),
);

const GROUP_OF: Record<string, string> = Object.fromEntries(
  MARKET_GROUPS.flatMap((group) => group.tags.map((tag) => [tag.code, group.id])),
);

/** Старые фишки (одно семейство) → новые метки: сохранённое сито не теряет смысл. */
const LEGACY: Record<string, readonly string[]> = {
  total: ['ttl_maps', 'ttl_pts', 'ittl'],
  handicap: ['hcp_maps', 'hcp_pts'],
};

export function migrateMarkets(codes: readonly string[]): string[] {
  return [...new Set(codes.flatMap((code) => LEGACY[code] ?? [code]))].filter((code) => code in MARKET_NAMES);
}

const KIND_OF: Record<string, (scope: string) => string> = {
  WIN: () => 'win',
  HCP: (scope) => (scope === 'PE' ? 'hcp_maps' : 'hcp_pts'),
  TTL: (scope) => (scope === 'PE' ? 'ttl_maps' : 'ttl_pts'),
  ITTL: () => 'ittl',
  RACE: () => 'race',
  NTH: () => 'nth',
  PROP: () => 'prop',
  CRN: () => 'corners',
  CRD: () => 'cards',
  BTTS: () => 'btts',
};

function spanTags(raw: string, sport: string | undefined, out: Set<string>): void {
  const tokens = raw.split(':');
  // Сырые формы зеркала: `WIN::PS:k:n:…` (сегмент карты k) и `…:P:k:…` (карта k).
  if (tokens[2] === 'PS') {
    out.add('segment');
    const k = Number(tokens[3]);
    if (k >= 1 && k <= 5) out.add(`map${k}`);
    return;
  }
  if (tokens[2] === 'P' && /^[1-5]$/.test(tokens[3] ?? '')) {
    out.add(`map${tokens[3]}`);
    return;
  }
  const period = marketPeriod(raw);
  if (period === 'M' || period === 'U') {
    out.add('whole');
    return;
  }
  if (period !== null) {
    const map = /^P([1-5])/.exec(period);
    if (map && !isSetSport(sport)) out.add(`map${map[1]}`);
    else out.add('part');
    return;
  }
  if (isMap4(raw, sport)) out.add('map4');
  else if (isMap3(raw, sport)) out.add('map3');
}

/** Все метки вилки. Пустого набора не бывает: непонятное — `other`. */
export function marketTags(fork: Pick<Fork, 'market' | 'sport'>): Set<string> {
  const raw = typeof fork.market === 'string' ? fork.market : '';
  const out = new Set<string>();
  const tokens = raw.split(':');
  const kind = KIND_OF[tokens[0] ?? ''];
  if (kind !== undefined && tokens.length >= 3) {
    out.add(kind(tokens[1] ?? ''));
  } else {
    const m = raw.toLowerCase();
    if (/инд\.?\s*тотал|individual total|team total/.test(m)) out.add('ittl');
    else if (m.includes('тотал') || m.includes('total')) out.add(/карт|maps/.test(m) ? 'ttl_maps' : 'ttl_pts');
    else if (m.includes('гандикап') || m.includes('фора') || m.includes('handicap')) out.add(/карт|maps/.test(m) ? 'hcp_maps' : 'hcp_pts');
    else if (m.includes('двойной шанс') || m.includes('double chance') || m.includes('ничья или')) out.add('double');
    else if (/победит|победа|winner|draw no bet|[23]-way/.test(m)) out.add('win');
  }
  spanTags(raw, fork.sport, out);
  if (![...out].some((tag) => GROUP_OF[tag] === 'kind' || GROUP_OF[tag] === 'odd')) out.add('other');
  return out;
}

/**
 * «Все, кроме» — любая выбранная метка прячет. «Только эти» — по каждой оси,
 * где что-то выбрано, у вилки должна быть метка из выбранных («тотал» + «карта 2»
 * = тоталы второй карты).
 */
export function marketPasses(tags: ReadonlySet<string>, chosen: readonly string[], mode: MarketMode): boolean {
  if (chosen.length === 0) return true;
  if (mode === 'except') return !chosen.some((code) => tags.has(code));
  const byGroup = new Map<string, string[]>();
  for (const code of chosen) {
    const group = GROUP_OF[code];
    if (group === undefined) continue;
    byGroup.set(group, [...(byGroup.get(group) ?? []), code]);
  }
  for (const codes of byGroup.values()) {
    if (!codes.some((code) => tags.has(code))) return false;
  }
  return true;
}
